import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  useGetCostSummaryQuery,
  useGetCostMonthlySummaryQuery,
  useGetCostTrendsQuery,
  useGetCostTopScansQuery,
  useGetCostFilterOptionsQuery,
} from "../../app/api/console/consoleCostAnalyticsApiSlice";
import CostTrendsChart from "../../components/CostAnalytics/CostTrendsChart";
import CostBreakdownChart from "../../components/CostAnalytics/CostBreakdownChart";
import { summaryDimensionToBreakdown } from "../../components/CostAnalytics/summaryBreakdownData";
import { costAnalyticsLoadingState } from "../../components/CostAnalytics/loadingState";
import CostTable from "../../components/CostAnalytics/CostTable";
import ErrorBoundary from "../../components/common/ErrorBoundary";
import AnalyticsWarmingBanner from "../../components/common/AnalyticsWarmingBanner";
import MultiSelectDropdown from "../../components/common/MultiSelectDropdown";

// Cap on chart series — the caller/purpose dimensions are unbounded (every
// eval run mints a new caller); the backend folds everything below the top
// N into "other" so the stacked chart stays renderable.
const TRENDS_TOP_N = 14;

// Keep the scan table payload under Sentry's Large HTTP payload detector
// (~300KB). At ~550B/row, 5000 scans were ~2.7MB (ZENYT-CONSOLE-FRONTEND-PROD-7).
// 250 rows ≈ 140KB and still cover ~16 pages of the client-side table.
// Full-period data remains available via Export.
export const COST_ANALYTICS_TOP_SCANS_LIMIT = 250;

const ENV_OPTIONS = [
  { value: null, label: "All Envs" },
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
  { value: "dev-cursor", label: "Dev-Cursor" },
  { value: "dev-raphrzb", label: "Dev-Raph" },
  { value: "dev-theo", label: "Dev-Theo" },
  { value: "dev-tom", label: "Dev-Tom" },
];

const SCAN_MODE_OPTIONS = [
  { value: null, label: "All Types" },
  { value: "scraper_one_look", label: "Scraper One Look" },
  { value: "agentic_test", label: "Agentic Test" },
  { value: "agentic_test_definition", label: "Test Definition" },
  { value: "multi_step_test", label: "Multi Step Test" },
];

const TRENDS_GROUP_OPTIONS = [
  { value: "purpose", label: "By Purpose" },
  { value: "caller", label: "By Caller" },
  { value: "model", label: "By Model" },
  { value: "env", label: "By Env" },
];

const DATE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function toDateStr(d) {
  return d.toISOString().split("T")[0];
}

// Website filter driven by /cost-analytics/filter-options (id + name), so the
// selection can be passed server-side to every endpoint instead of being a
// client-side filter that only applied to the scan table.
function WebsiteDropdown({ websites, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const sorted = useMemo(() => {
    if (!websites?.length) return [];
    return [...websites].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  }, [websites]);

  if (!sorted.length) return null;

  const selectedName = selected
    ? sorted.find((w) => w.id === selected)?.name || selected
    : null;

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 text-body-xs font-medium rounded-lg border transition-all ${
          selected
            ? "border-accent-purple bg-accent-purple/5 text-accent-purple"
            : "border-border-subtle text-text-secondary hover:border-border-default"
        }`}
      >
        <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
          <path strokeLinecap='round' strokeLinejoin='round' d='M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9' />
        </svg>
        <span className='truncate max-w-[160px]'>{selectedName || "All Websites"}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
        </svg>
      </button>
      {open && (
        <div className='absolute z-50 mt-1 w-64 max-h-72 overflow-y-auto bg-white rounded-xl border border-border-subtle shadow-lg py-1'>
          <button
            onClick={() => { onSelect(null); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-body-xs hover:bg-surface-neutral transition-colors ${
              !selected ? "font-semibold text-accent-purple" : "text-text-primary"
            }`}
          >
            All Websites
          </button>
          {sorted.map(({ id, name }) => (
            <button
              key={id}
              onClick={() => { onSelect(id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-body-xs hover:bg-surface-neutral transition-colors ${
                selected === id ? "font-semibold text-accent-purple" : "text-text-primary"
              }`}
            >
              <span className='truncate'>{name || id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Generic single-select filter dropdown driven by an array of {value, label}
// options coming from /cost-analytics/filter-options. Used for caller and
// purpose filters.
function FilterDropdown({ icon, allLabel, options, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (!options?.length) return null;

  return (
    <div className='relative' ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-2 text-body-xs font-medium rounded-lg border transition-all ${
          selected
            ? "border-accent-purple bg-accent-purple/5 text-accent-purple"
            : "border-border-subtle text-text-secondary hover:border-border-default"
        }`}
      >
        {icon}
        {selected || allLabel}
        <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
          <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
        </svg>
      </button>
      {open && (
        <div className='absolute z-50 mt-1 w-64 max-h-72 overflow-y-auto bg-white rounded-xl border border-border-subtle shadow-lg py-1'>
          <button
            onClick={() => { onSelect(null); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-body-xs hover:bg-surface-neutral transition-colors ${
              !selected ? "font-semibold text-accent-purple" : "text-text-primary"
            }`}
          >
            {allLabel}
          </button>
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onSelect(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-body-xs hover:bg-surface-neutral transition-colors ${
                selected === opt ? "font-semibold text-accent-purple" : "text-text-primary"
              }`}
            >
              <span className='truncate'>{opt}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CALLER_ICON = (
  <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
  </svg>
);

const PURPOSE_ICON = (
  <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path strokeLinecap='round' strokeLinejoin='round' d='M13 10V3L4 14h7v7l9-11h-7z' />
  </svg>
);

function CostAnalytics() {
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [selectedScanMode, setSelectedScanMode] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [selectedCallers, setSelectedCallers] = useState([]);
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [trendsGroupBy, setTrendsGroupBy] = useState("purpose");
  const [datePreset, setDatePreset] = useState(30);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const dateRange = useMemo(() => {
    if (customStart && customEnd) {
      return { startDate: customStart, endDate: customEnd };
    }
    const end = new Date();
    const start = new Date(Date.now() - datePreset * 24 * 60 * 60 * 1000);
    return { startDate: toDateStr(start), endDate: toDateStr(end) };
  }, [datePreset, customStart, customEnd]);

  const selectPreset = (days) => {
    setDatePreset(days);
    setCustomStart("");
    setCustomEnd("");
  };

  const [pollMs, setPollMs] = useState(0);

  // Drives caller, purpose, and website dropdown options. Scoped to the
  // current date range so we only show values that have actually been active.
  const { data: filterOptions, error: filterOptionsError } = useGetCostFilterOptionsQuery({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  }, { pollingInterval: pollMs });

  // Empty / all-selected → no server filter (equivalent to All Callers).
  const activeCallers = useMemo(() => {
    const options = filterOptions?.callers || [];
    if (!selectedCallers.length) return undefined;
    if (options.length && selectedCallers.length === options.length) return undefined;
    return selectedCallers;
  }, [selectedCallers, filterOptions?.callers]);

  // Every section below receives the exact same filter set, server-side, so
  // the chart totals, breakdowns, KPI cards, and the scan table always agree.
  const commonFilters = useMemo(() => ({
    env: selectedEnv || undefined,
    caller: activeCallers,
    purpose: selectedPurpose || undefined,
    scanMode: selectedScanMode || undefined,
    websiteId: selectedWebsite || undefined,
  }), [selectedEnv, activeCallers, selectedPurpose, selectedScanMode, selectedWebsite]);

  const { data: monthlyData, isLoading: monthlyLoading, error: monthlyError } = useGetCostMonthlySummaryQuery({
    ...dateRange,
    ...commonFilters,
  }, {
    pollingInterval: pollMs,
  });

  const { data: trendsData, isLoading: trendsLoading, error: trendsError } = useGetCostTrendsQuery({
    ...dateRange,
    ...commonFilters,
    groupBy: trendsGroupBy,
    topN: TRENDS_TOP_N,
  }, { pollingInterval: pollMs });

  const { data: topScansData, isLoading: topScansLoading, error: topScansError } = useGetCostTopScansQuery({
    ...dateRange,
    ...commonFilters,
    limit: COST_ANALYTICS_TOP_SCANS_LIMIT,
    sortBy: "date",
  }, { pollingInterval: pollMs });

  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useGetCostSummaryQuery({
    ...dateRange,
    ...commonFilters,
  }, { pollingInterval: pollMs });

  const { isWarming, progress: warmingProgress, error: loadError } = costAnalyticsLoadingState([
    { data: monthlyData, error: monthlyError },
    { data: filterOptions, error: filterOptionsError },
    { data: trendsData, error: trendsError },
    { data: topScansData, error: topScansError },
    { data: summaryData, error: summaryError },
  ]);
  const exportDisabled = isWarming || !!loadError;

  useEffect(() => {
    setPollMs(isWarming ? 2000 : 0);
  }, [isWarming]);

  const summaryBreakdowns = useMemo(() => ({
    purpose: summaryDimensionToBreakdown(summaryData, "purpose"),
    caller: summaryDimensionToBreakdown(summaryData, "caller"),
    model: summaryDimensionToBreakdown(summaryData, "model"),
  }), [summaryData]);

  const periodTotal = useMemo(() => {
    if (!trendsData?.trends?.length) return null;
    return trendsData.trends.reduce((s, d) => s + (d.total || 0), 0);
  }, [trendsData]);

  const envLabel = useMemo(() => {
    if (!selectedEnv) return "all envs";
    return ENV_OPTIONS.find((o) => o.value === selectedEnv)?.label?.toLowerCase() || selectedEnv;
  }, [selectedEnv]);

  const kpiPeriodLabel = useMemo(() => {
    const filters = [];
    if (selectedEnv) filters.push(envLabel);
    else filters.push("all envs");
    if (selectedScanMode) {
      const mode = SCAN_MODE_OPTIONS.find((o) => o.value === selectedScanMode);
      filters.push(mode?.label || selectedScanMode);
    }
    if (selectedCallers.length) filters.push(selectedCallers.join(", "));
    else filters.push("all callers");
    if (selectedPurpose) filters.push(selectedPurpose);
    if (selectedWebsite) {
      const site = filterOptions?.websites?.find((w) => w.id === selectedWebsite);
      filters.push(site?.name || selectedWebsite);
    }
    return `Selected period (${filters.join(" · ")})`;
  }, [
    envLabel,
    selectedEnv,
    selectedScanMode,
    selectedCallers,
    selectedPurpose,
    selectedWebsite,
    filterOptions,
  ]);

  const buildExportUrl = (format) => {
    const params = new URLSearchParams();
    params.append("start_date", dateRange.startDate);
    params.append("end_date", dateRange.endDate);
    if (selectedEnv) params.append("env", selectedEnv);
    if (activeCallers?.length) {
      activeCallers.forEach((c) => params.append("caller", c));
    }
    if (selectedPurpose) params.append("purpose", selectedPurpose);
    if (selectedScanMode) params.append("scan_mode", selectedScanMode);
    if (selectedWebsite) params.append("website_id", selectedWebsite);
    const token = localStorage.getItem("token");
    if (token) params.append("token", token);
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8020";
    const path = format === "xlsx" ? "export-excel" : "export";
    return `${baseUrl}/cost-analytics/${path}?${params.toString()}`;
  };

  const handleExport = (format) => {
    if (exportDisabled) {
      setExportOpen(false);
      return;
    }
    window.open(buildExportUrl(format), "_blank");
    setExportOpen(false);
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-h2 text-text-primary'>Cost Analytics</h1>
          <p className='text-body-sm text-text-secondary mt-1'>
            LLM usage costs across environments and services
          </p>
          <p className='text-caption text-text-tertiary mt-1 max-w-2xl'>
            All Envs includes Staging, Production, and Dev stacks. Totals count every
            caller (team + eval + other). Team Usage is the staging/prod attribution
            view for known staff identities only.
          </p>
        </div>
        <div className='relative' ref={exportRef}>
          <button
            onClick={() => !exportDisabled && setExportOpen(!exportOpen)}
            disabled={exportDisabled}
            title={exportDisabled ? "Wait for cost data to finish loading before exporting" : "Export"}
            className={`flex items-center gap-2 px-4 py-2 text-body-xs font-medium rounded-lg border border-border-subtle transition-colors ${
              exportDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-surface-neutral"
            }`}
          >
            <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
            </svg>
            {isWarming ? "Export (loading…)" : "Export"}
            <svg className={`w-3 h-3 transition-transform ${exportOpen ? "rotate-180" : ""}`} fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
            </svg>
          </button>
          {exportOpen && !exportDisabled && (
            <div className='absolute right-0 z-50 mt-1 w-64 bg-white rounded-xl border border-border-subtle shadow-lg py-1'>
              <button
                onClick={() => handleExport("xlsx")}
                className='w-full text-left px-3 py-2.5 hover:bg-surface-neutral transition-colors'
              >
                <span className='block text-body-xs font-semibold text-text-primary'>Excel workbook (.xlsx)</span>
                <span className='block text-caption text-text-tertiary mt-0.5'>
                  Summary, daily, by purpose / caller / model / env / website / tag, and all scans
                </span>
              </button>
              <button
                onClick={() => handleExport("csv")}
                className='w-full text-left px-3 py-2.5 hover:bg-surface-neutral transition-colors'
              >
                <span className='block text-body-xs font-semibold text-text-primary'>Scans CSV</span>
                <span className='block text-caption text-text-tertiary mt-0.5'>One row per scan with per-purpose costs</span>
              </button>
              <div className='px-3 py-1.5 border-t border-border-subtle mt-1'>
                <span className='text-caption text-text-tertiary'>Exports respect the active filters</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters first so env/date scope is set before reading headline totals */}
      <div className='flex items-center gap-3 flex-wrap'>
        {/* Date range */}
        <div className='flex items-center gap-1 bg-surface-neutral rounded-xl p-0.5'>
          {DATE_PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => selectPreset(p.days)}
              className={`px-3 py-2 text-body-xs font-medium rounded-lg transition-all ${
                datePreset === p.days && !customStart
                  ? "bg-white text-text-primary shadow-sm"
                  : "text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className='flex items-center gap-1.5'>
          <input
            type='date'
            value={customStart || dateRange.startDate}
            onChange={(e) => {
              setCustomStart(e.target.value);
              if (!customEnd) setCustomEnd(dateRange.endDate);
            }}
            className='px-2.5 py-1.5 text-body-xs rounded-lg border border-border-subtle bg-white text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-purple'
          />
          <span className='text-body-xs text-text-tertiary'>–</span>
          <input
            type='date'
            value={customEnd || dateRange.endDate}
            onChange={(e) => {
              setCustomEnd(e.target.value);
              if (!customStart) setCustomStart(dateRange.startDate);
            }}
            className='px-2.5 py-1.5 text-body-xs rounded-lg border border-border-subtle bg-white text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-purple'
          />
        </div>

        <div className='w-px h-6 bg-border-subtle' />

        {/* Environment */}
        <div className='flex items-center gap-1 bg-surface-neutral rounded-xl p-0.5'>
          {ENV_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              onClick={() => setSelectedEnv(opt.value)}
              className={`px-3 py-2 text-body-xs font-medium rounded-lg transition-all ${
                selectedEnv === opt.value
                  ? "bg-white text-text-primary shadow-sm"
                  : "text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className='w-px h-6 bg-border-subtle' />

        {/* Scan mode */}
        <div className='flex items-center gap-1 bg-surface-neutral rounded-xl p-0.5'>
          {SCAN_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              onClick={() => setSelectedScanMode(opt.value)}
              className={`px-3 py-2 text-body-xs font-medium rounded-lg transition-all ${
                selectedScanMode === opt.value
                  ? "bg-white text-text-primary shadow-sm"
                  : "text-text-tertiary hover:text-text-secondary"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className='w-px h-6 bg-border-subtle' />

        {/* Caller — multi-select (OR filter across selected identities) */}
        {filterOptions?.callers?.length > 0 && (
          <MultiSelectDropdown
            icon={CALLER_ICON}
            placeholder='All Callers'
            options={filterOptions.callers}
            selected={selectedCallers}
            onChange={setSelectedCallers}
            compact
          />
        )}

        {/* Purpose (canonical "what step" — required on every llm-usage row) */}
        <FilterDropdown
          icon={PURPOSE_ICON}
          allLabel='All Purposes'
          options={filterOptions?.purposes}
          selected={selectedPurpose}
          onSelect={setSelectedPurpose}
        />

        <div className='w-px h-6 bg-border-subtle' />

        {/* Website / company */}
        <WebsiteDropdown
          websites={filterOptions?.websites}
          selected={selectedWebsite}
          onSelect={setSelectedWebsite}
        />
      </div>

      {/* Monthly stat cards */}
      {loadError ? (
        <div role='alert' className='rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800'>
          <p>{loadError}</p>
          <button className='mt-3 text-sm font-medium underline' onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      ) : isWarming ? (
        <AnalyticsWarmingBanner
          label='Loading cost analytics…'
          progress={warmingProgress}
        />
      ) : (
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {monthlyLoading ? (
          <>
            {[1, 2, 3].map((i) => (
              <div key={i} className='bg-white rounded-2xl border border-border-subtle p-5 animate-pulse'>
                <div className='h-3 bg-surface-neutral rounded w-24 mb-3' />
                <div className='h-8 bg-surface-neutral rounded w-20' />
              </div>
            ))}
          </>
        ) : (
          <>
            <div className='bg-white rounded-2xl border border-border-subtle p-5'>
              <p className='text-body-xs font-medium text-text-secondary mb-1'>{kpiPeriodLabel}</p>
              <span className='text-metric text-accent-purple'>
                ${monthlyData?.current_month_cost?.toFixed(2) ?? "—"}
              </span>
              <p className='text-caption text-text-tertiary mt-1'>
                Prior period: ${monthlyData?.previous_month_cost?.toFixed(2) ?? "—"}
                {monthlyData?.earliest_date && (
                  <span className='ml-2 text-text-tertiary/60'>· data since {monthlyData.earliest_date}</span>
                )}
              </p>
              {!selectedEnv && !selectedCallers.length && (
                <p className='text-caption text-text-tertiary mt-2 leading-snug'>
                  Not comparable to Team Usage Team Total. Select{" "}
                  <span className='font-medium text-text-secondary'>Staging</span>
                  {" "}here to match Team Usage&apos;s env total; Team Total still
                  excludes eval/other callers.
                </p>
              )}
              {selectedEnv && !selectedCallers.length && (
                <p className='text-caption text-text-tertiary mt-2 leading-snug'>
                  Matches Team Usage&apos;s{" "}
                  <span className='font-medium text-text-secondary'>
                    {selectedEnv === "production" ? "Production" : "Staging"} env total
                  </span>
                  . Team Total is lower because it counts known staff identities only.
                </p>
              )}
            </div>
            <div className='bg-white rounded-2xl border border-border-subtle p-5'>
              <p className='text-body-xs font-medium text-text-secondary mb-1'>Avg Cost / Scan</p>
              <span className='text-metric text-accent-green'>
                ${monthlyData?.avg_cost_per_scan?.toFixed(2) ?? "—"}
              </span>
              <p className='text-caption text-text-tertiary mt-1'>
                {monthlyData?.unique_scans ?? 0} scans in period
              </p>
            </div>
            <div className='bg-white rounded-2xl border border-border-subtle p-5'>
              <p className='text-body-xs font-medium text-text-secondary mb-1'>Avg Cost / 100 Items</p>
              <span className='text-metric text-blue-500'>
                ${monthlyData?.avg_cost_per_100_items?.toFixed(2) ?? "—"}
              </span>
              <p className='text-caption text-text-tertiary mt-1'>
                {monthlyData?.total_items?.toLocaleString() ?? 0} pages/tests in period
              </p>
            </div>
          </>
        )}
      </div>
      )}

      {/* Daily spending stacked bar chart — hidden while the rollup cache
          is still warming so we don't flash "Failed to load" / empty charts. */}
      {!isWarming && !loadError && (
      <div className='bg-white rounded-2xl border border-border-subtle p-6'>
        <div className='flex items-center justify-between mb-5'>
          <div className='flex items-baseline gap-3'>
            <h2 className='text-body-lg font-semibold text-text-primary'>Daily Spendings</h2>
            {periodTotal != null && (
              <span className='text-body-xs text-text-secondary'>
                <span className='font-semibold text-text-primary'>${periodTotal.toFixed(2)}</span> total for period
              </span>
            )}
          </div>
          <div className='flex items-center gap-1 bg-surface-neutral rounded-xl p-0.5'>
            {TRENDS_GROUP_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTrendsGroupBy(opt.value)}
                className={`px-3 py-1.5 text-body-xs font-medium rounded-lg transition-all ${
                  trendsGroupBy === opt.value
                    ? "bg-white text-text-primary shadow-sm"
                    : "text-text-tertiary hover:text-text-secondary"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <ErrorBoundary name='Daily Spendings'>
          <CostTrendsChart data={trendsData} isLoading={trendsLoading} error={trendsError} />
        </ErrorBoundary>
      </div>
      )}

      {/* Per-purpose, per-caller, and per-model breakdowns — all share the
          page's date range and filters so their totals always agree. */}
      {!isWarming && !loadError && (
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {["purpose", "caller", "model"].map((dim) => (
          <div key={dim} className='bg-white rounded-2xl border border-border-subtle p-6'>
            <ErrorBoundary name={`Spending by ${dim}`}>
              <CostBreakdownChart
                data={summaryBreakdowns[dim]}
                isLoading={summaryLoading}
                error={summaryError}
                groupBy={dim}
              />
            </ErrorBoundary>
          </div>
        ))}
      </div>
      )}

      {/* Scan list table */}
      {!isWarming && !loadError && (
      <div className='bg-white rounded-2xl border border-border-subtle p-6'>
        <h2 className='text-body-lg font-semibold text-text-primary mb-4'>Scans</h2>
        <ErrorBoundary name='Scans table'>
          <CostTable
            data={topScansData}
            isLoading={topScansLoading}
            error={topScansError}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            limit={COST_ANALYTICS_TOP_SCANS_LIMIT}
          />
        </ErrorBoundary>
      </div>
      )}
    </div>
  );
}

export default CostAnalytics;
